import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import AttachmentUploadSection from '../../attachment-upload-section.gts';
import { DrawerHeader, DrawerBody } from 'tio-ui/components/drawer';
import AttachmentsAnalysisView from '../../approval/drawers/ai-analysis/attachments-analysis-view.gts';
import AttachmentList from '../../../tuition-assistance/forms/upload-attachments/attachment-list.gts';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type { Store as StoreService } from '@warp-drive/core';
import { findRecord } from '@warp-drive/utilities/json-api';
import { reloadRecord, saveRecord, deleteExistingRecord } from 'tio-data/requests/save-record';
import { AiAnalysisWatcher } from '../../../../utils/tuition-assistance/ai-analysis-watcher.ts';
import AiReadableFileTypes from '../../../../helpers/ai-readable-file-types.ts';
import type CableService from '../../../../services/cable.ts';
import type { DocumentType } from 'tio-data/types/tuition-assistance';

const CLASSIFICATION_OPTIONS = [
  { label: 'Program Approval Documents', key: 'PROGRAM_APPROVAL_DOCUMENTS' },
  { label: 'Program Completion Documents', key: 'PROGRAM_COMPLETION_DOCUMENTS' },
];

interface ProgramAttachmentsDrawerSignature {
  Args: {
    instance: TasProgramInstanceModel;
    allAttachments: TasAssetModel[];
    disabled?: boolean;
    hideDiscarded?: boolean;
  };
}

export default class ProgramAttachmentsDrawer extends Component<ProgramAttachmentsDrawerSignature> {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;
  @service declare cable: CableService;

  @tracked showUploadSection = false;
  @tracked selectedClassification = 'PROGRAM_APPROVAL_DOCUMENTS';
  @tracked selectedCategory: string | null = null;

  /** Resolves AI verdicts for drawer uploads (subscribe → evaluate → persist). */
  #aiWatcher: AiAnalysisWatcher;

  constructor(owner: Owner, args: ProgramAttachmentsDrawerSignature['Args']) {
    super(owner, args);
    // Built here rather than as a field initializer — the injected services
    // are not readable until construction.
    this.#aiWatcher = new AiAnalysisWatcher({
      cable: this.cable,
      contextFor: (declaredType, uploadRowKey, asset) =>
        this.#aiContextFor(declaredType, uploadRowKey, asset),
      persist: (asset) => saveRecord(this.store, asset),
      reload: (asset) => {
        if (!asset.id) return Promise.resolve(null);
        return this.store.request(
          findRecord<TasAssetModel>('tas-asset', asset.id, {
            include: 'asset-summary',
            reload: true,
          })
        );
      },
    });
    // Heal documents whose verdict never landed (the uploading surface closed
    // before the AI broadcast): when the summary is already persisted,
    // evaluate it now. Deferred past render so tag mutation cannot invalidate
    // values consumed earlier in this same render pass.
    // eslint-disable-next-line ember/no-runloop
    scheduleOnce('afterRender', this, this.#recoverPendingVerdicts);
  }

  willDestroy() {
    super.willDestroy();
    this.#aiWatcher.teardown();
  }

  #aiContextFor(declaredType: DocumentType, uploadRowKey: string, asset?: TasAssetModel) {
    const instance = this.args.instance;
    // Prefer the asset's own scope (course- or application-attached docs
    // recovered from other surfaces) over the instance-wide aggregate, so a
    // doc can't pass by matching a sibling course/application.
    const assetCourseNames = (asset?.tasCourses ?? []).map((course) => course.courseName);
    const assetApplicationCourseNames = (asset?.tasApplications ?? []).flatMap((application) =>
      application.activeCourses.map((course) => course.courseName)
    );
    const aggregateCourseNames = instance.tasApplications.flatMap((application) =>
      application.activeCourses.map((course) => course.courseName)
    );
    const expectedCourseNames = (
      assetCourseNames.length
        ? assetCourseNames
        : assetApplicationCourseNames.length
          ? assetApplicationCourseNames
          : aggregateCourseNames
    ).filter(Boolean);
    return {
      declaredType,
      uploadRowKey,
      employeeName: instance.employee?.person?.fullName ?? '',
      expectedInstitutionName: instance.institutionName ?? '',
      expectedCourseNames,
      translate: (key: string, options?: Record<string, string>) => this.intl.t(key, options),
    };
  }

  #recoverPendingVerdicts = () => {
    if (this.isDestroying || this.isDestroyed) return;
    void this.#aiWatcher.recoverPending(this.args.allAttachments ?? []);
  };

  get sortedAttachments(): TasAssetModel[] {
    const assets = this.args.allAttachments ?? [];
    const notDeleted = assets
      .filter((a) => a.attachmentDeletedAt === null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    if (this.args.hideDiscarded) {
      return notDeleted;
    }

    const deleted = assets.filter((a) => a.attachmentDeletedAt !== null);
    return [...notDeleted, ...deleted];
  }

  get assetSummaries() {
    return this.args.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary) => summary !== null);
  }

  get canUpload(): boolean {
    return !this.args.disabled;
  }

  /**
   * Admin-configured document entries (label + AI document type) for the
   * currently selected classification, from the template's
   * `*_ATTACHMENT_FIELD`. Empty for legacy templates with no configured types.
   */
  get documentEntries() {
    return (
      this.args.instance.tasProgramTemplate?.documentEntriesFor(this.selectedClassification) ?? []
    );
  }

  get categoryOptions() {
    // Legacy templates can have attachment-field labels with no document-type
    // map — documentEntriesFor() resolves those all to 'general'. Mirror the
    // employee flow's fallback signal: only offer (and require) categories
    // when the template declares at least one AI-classifiable type.
    const template = this.args.instance.tasProgramTemplate;
    if (!template?.requiredDocumentTypesFor(this.selectedClassification).length) {
      return [];
    }
    return this.documentEntries.map((entry) => ({ label: entry.label, key: entry.label }));
  }

  // When the template defines categories for the classification, picking one is required.
  get uploadDisabled() {
    return this.categoryOptions.length > 0 && !this.selectedCategory;
  }

  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
  };

  // Categories are per-classification, so reset on change
  classificationDidChange = (key: string) => {
    this.selectedClassification = key;
    this.selectedCategory = null;
  };

  categoryDidChange = (key: string) => {
    this.selectedCategory = key;
  };

  isDeleted = (asset: TasAssetModel): boolean => {
    return asset.attachmentDeletedAt !== null;
  };

  uploadFile = enqueueTask(async (file) => {
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file);
    asset.classification = this.selectedClassification;

    // Tag with the selected category the same way the employee categorized
    // upload flow does (tuition-assistance/forms/categorized-upload) so the
    // document counts toward the configured required-document rows and the AI
    // pipeline picks it up. Tags must be set before the first save — the API
    // decides whether to dispatch AI analysis when the asset is created. No
    // category (legacy templates) → 'general': marked valid immediately and
    // AI analysis is skipped.
    const entry = this.documentEntries.find((e) => e.label === this.selectedCategory);
    const documentType = entry?.type ?? 'general';
    asset.tags = {
      ...(entry ? { uploadRowKey: entry.label } : {}),
      employeeSelectedType: documentType,
      aiValidationStatus: documentType === 'general' ? 'valid' : 'pending',
      aiValidationDisplayStatus: documentType === 'general' ? 'valid' : 'pending',
      skipAiAnalysis: String(documentType === 'general'),
    };

    const instance = this.args.instance;

    asset.tasProgramInstances.push(instance);
    instance.tasAssets.push(asset);

    // Watch for the AI verdict BEFORE the create request dispatches analysis —
    // subscribing after the saves leaves a gap where a fast broadcast could be
    // missed, stranding the asset at `pending` until the timeout fallback.
    if (entry && documentType !== 'general') {
      this.#aiWatcher.watch(asset, entry.label, documentType);
    }
    try {
      await saveRecord(this.store, asset);
    } catch (e) {
      // The create never happened, so no analysis was dispatched — drop the
      // watch. A failing instance save below must NOT unwatch: the created
      // asset has already dispatched analysis and its verdict should land.
      this.#aiWatcher.unwatch(asset.uuid);
      throw e;
    }
    // The create settled — buffered verdicts may now persist safely.
    this.#aiWatcher.markCreated(asset.uuid);
    await saveRecord(this.store, instance);

    this.showUploadSection = false;
  });

  deleteAttachment = enqueueTask(async (asset: TasAssetModel) => {
    const attachment = asset.attachments[0];
    if (!attachment) {
      return;
    }
    await deleteExistingRecord(this.store, attachment);
    await reloadRecord(this.store, asset);
  });

  <template>
    <DrawerHeader>
      <h2 class="tio-text-heading tio-fg-primary">
        {{t "common.tas.approval.drawer.attachments.title" default="Attachments"}}
      </h2>
    </DrawerHeader>

    <DrawerBody>
      <div class="tio-col-extra-loose p-6">
        <AttachmentUploadSection
          @canUpload={{this.canUpload}}
          @showUploadSection={{this.showUploadSection}}
          @toggleUploadSection={{this.toggleUploadSection}}
          @selectedClassification={{this.selectedClassification}}
          @classificationDidChange={{this.classificationDidChange}}
          @uploadFile={{this.uploadFile}}
          @classificationOptions={{CLASSIFICATION_OPTIONS}}
          @categoryOptions={{this.categoryOptions}}
          @selectedCategory={{this.selectedCategory}}
          @categoryDidChange={{this.categoryDidChange}}
          @uploadDisabled={{this.uploadDisabled}}
          @accept={{AiReadableFileTypes "accept"}}
        />

        {{#if @hideDiscarded}}
          <AttachmentList
            @assets={{this.sortedAttachments}}
            @disabled={{@disabled}}
            @hideDiscarded={{true}}
            class="w-full"
          />
        {{else}}
          <AttachmentsAnalysisView
            @attachments={{this.sortedAttachments}}
            @assetSummaries={{this.assetSummaries}}
            @deleteAttachment={{this.deleteAttachment}}
            @isDeleted={{this.isDeleted}}
          />
        {{/if}}
      </div>
    </DrawerBody>
  </template>
}
